/**
 * @fileoverview WebSocket client for the Farmeando Aura Admin Operator Panel.
 * Connects to the platform's real-time communication channel to receive telemetry,
 * snapshots, game state transitions, and emit operator commands.
 * 
 * @module utilities/WS/Admin/FarmeandoAuraWS
 */

let farmeandoAuraAdminWebSocket = null;
let isFarmeandoAuraAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the Farmeando Aura admin panel.
 * 
 * @function ConnectWebSocketForFarmeandoAuraAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when the connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForFarmeandoAuraAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (farmeandoAuraAdminWebSocket || isFarmeandoAuraAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            farmeandoAuraAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            farmeandoAuraAdminWebSocket.onopen = () => {
                isFarmeandoAuraAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');
            };

            farmeandoAuraAdminWebSocket.onclose = () => {
                isFarmeandoAuraAdminConnected = false;
                farmeandoAuraAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            farmeandoAuraAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            farmeandoAuraAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Farmeando Aura admin WebSocket connection.
 * 
 * @function SendWebSocketMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendWebSocketMessage = (payloadObject) => {
    try {
        if (farmeandoAuraAdminWebSocket && farmeandoAuraAdminWebSocket.readyState === WebSocket.OPEN) {
            farmeandoAuraAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the Farmeando Aura admin WebSocket connection.
 * 
 * @function DisconnectWebSocketForFarmeandoAuraAdmin
 */
export const DisconnectWebSocketForFarmeandoAuraAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (farmeandoAuraAdminWebSocket) {
            farmeandoAuraAdminWebSocket.onopen = null;
            farmeandoAuraAdminWebSocket.onclose = null;
            farmeandoAuraAdminWebSocket.onerror = null;
            farmeandoAuraAdminWebSocket.onmessage = null;

            if (farmeandoAuraAdminWebSocket.readyState === WebSocket.OPEN) {
                farmeandoAuraAdminWebSocket.close();
            } else if (farmeandoAuraAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = farmeandoAuraAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            farmeandoAuraAdminWebSocket = null;
            isFarmeandoAuraAdminConnected = false;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
